import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Button,
  Container,
} from "reactstrap"
import Select from "react-select"
import CreatableSelect from "react-select/creatable"
import * as Yup from "yup"
import { useFormik } from "formik"
import SuccessPopup from "../../../components/SuccessPopup"
import ErrorPopup from "../../../components/ErrorPopup"
import {
  fetchPvsCategory,
  fetchPvsSubcategory,
  createProduct,
} from "../../../redux/actions/pvs"
import { CREATE_PRODUCT_RESET } from "../../../redux/types/pvs"
import { toast } from "react-toastify"

const AddProduct = () => {
  document.title = "Add Product | PVS | Admin Dashboard"

  const history = useNavigate()
  const dispatch = useDispatch()

  const [successOpen, setSuccessOpen] = useState(false)
  const [errorOpen, setErrorOpen] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")
  const [subcategoryOptions, setSubcategoryOptions] = useState([])
  const [inputValue, setInputValue] = useState("")

  const pvsState = useSelector(state => state.pvs)
  const createProductState = useSelector(
    state => state.screens.pvs.createProduct
  )

  // Options for Select
  const categoryOptions = (pvsState.categoryList || []).map(c => ({
    value: c.pvs_cat_id,
    label: c.category,
    encId: c.enc_pvs_cat_id,
  }))

  const createOption = value => ({ label: value, value })

  // Formik setup
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      product_name: [],
      category: null,
      subcategory: null,
    },
    validationSchema: Yup.object({
      product_name: Yup.array().min(1, "Please Enter Product Name"),
      category: Yup.object().required("Please Select Category"),
      subcategory: Yup.object().required("Please Select Subcategory"),
    }),
    onSubmit: values => {
      const requestData = {
        product_names: values.product_name.map(option => option.value),
        enc_pvs_cat_id: values.category.encId,
        enc_pvs_subcat_id: values.subcategory.encId,
      }

      dispatch(createProduct(requestData))
    },
  })

  // Handle category change to fetch subcategories
  const handleCategoryChange = selectedCategory => {
    validation.setFieldValue("category", selectedCategory)
    validation.setFieldValue("subcategory", null) // Reset subcategory

    if (selectedCategory) {
      dispatch(
        fetchPvsSubcategory({
          enc_pvs_cat_id: selectedCategory.encId,
        })
      )
    } else {
      setSubcategoryOptions([])
    }
  }

  const handleInputChange = value => {
    setInputValue(value)
  }

  const handleKeyDown = event => {
    if (!inputValue) return

    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()

      const values = inputValue
        .split(",")
        .map(v => v.trim())
        .filter(Boolean)

      if (!values.length) return

      const newOptions = values.map(createOption)
      const existingOptions = validation.values.product_name || []
      const combined = [...existingOptions, ...newOptions]
      const unique = Array.from(
        new Map(combined.map(item => [item.value, item])).values()
      )

      validation.setFieldValue("product_name", unique)
      setInputValue("")
    }
  }

  const handlePaste = event => {

    event.preventDefault()

    const pastedText = event.clipboardData.getData("text")
    const values = pastedText
      .split(",")
      .map(v => v.trim())
      .filter(Boolean)

    if (!values.length) return

    const newOptions = values.map(createOption)
    const existingOptions = validation.values.product_name || []
    const combined = [...existingOptions, ...newOptions]
    const unique = Array.from(
      new Map(combined.map(item => [item.value, item])).values()
    )

    validation.setFieldValue("product_name", unique)
    setInputValue("")
  }

  // Fetch categories on mount
  useEffect(() => {
    dispatch(fetchPvsCategory())
  }, [dispatch])

  // Update subcategory options when fetched
  useEffect(() => {
    if (pvsState.subcategoryList && pvsState.subcategoryList.length > 0) {
      const tempSubcategoryOption = pvsState.subcategoryList.map(option => ({
        label: option.subcategory,
        value: option.pvs_subcat_id,
        encId: option.enc_pvs_subcat_id,
      }))
      setSubcategoryOptions(tempSubcategoryOption)
    }
  }, [pvsState.subcategoryList])

  // Handle create success/error popups
  useEffect(() => {
    if (createProductState.success) {
      toast.success(createProductState.message || "Product created successfully!")
      dispatch({ type: CREATE_PRODUCT_RESET })
      history("/product-list", { replace: true })
    } else if (createProductState.error) {
      setErrorMsg(createProductState.message || "Unable to create product.")
      setErrorOpen(true)
    }
  }, [createProductState, dispatch])

  return (
    <React.Fragment>
      <SuccessPopup
        isOpen={successOpen}
        toggle={() => setSuccessOpen(false)}
        onConfirm={() => {
          setSuccessOpen(false)
          dispatch({ type: CREATE_PRODUCT_RESET })
          history("/product-list", { replace: true })
        }}
        message={
          (createProductState && createProductState.message) ||
          "Product created successfully!"
        }
      />
      <ErrorPopup
        isOpen={errorOpen}
        toggle={() => {
          setErrorOpen(false)
          dispatch({ type: CREATE_PRODUCT_RESET })
        }}
        message={errorMsg}
      />
      <div className="page-content">
        <Container fluid={true}>
          <div className="flex-grow-1">
            <h4 className="h4 nom-title mt-4 mb-4">Add Product</h4>
          </div>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Form
                    onSubmit={e => {
                      e.preventDefault()
                      validation.handleSubmit()
                      return false
                    }}
                    action="#"
                  >
                    <div className="row">
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="category">
                          Category <span className="text-danger">*</span>
                        </Label>
                        <Select
                          name="category"
                          value={validation.values.category}
                          onChange={handleCategoryChange}
                          options={categoryOptions}
                          classNamePrefix="select2-selection"
                          placeholder="Select Category"
                        />
                        {validation.touched.category &&
                        validation.errors.category ? (
                          <div className="text-danger">
                            {validation.errors.category}
                          </div>
                        ) : null}
                      </div>

                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="subcategory">
                          Subcategory <span className="text-danger">*</span>
                        </Label>
                        <Select
                          name="subcategory"
                          value={validation.values.subcategory}
                          onChange={selectedOptions =>
                            validation.setFieldValue(
                              "subcategory",
                              selectedOptions
                            )
                          }
                          options={subcategoryOptions}
                          classNamePrefix="select2-selection"
                          placeholder="Select Subcategory"
                        />
                        {validation.touched.subcategory &&
                        validation.errors.subcategory ? (
                          <div className="text-danger">
                            {validation.errors.subcategory}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className="row">
                      <div className="mb-3 col-md-6">
                        <Label className="form-label" htmlFor="product_name">
                          Product Name <span className="text-danger">*</span>
                        </Label>
                        <CreatableSelect
                          id="product_name"
                          name="product_name"
                          isMulti
                          placeholder="Paste & Enter"
                          options={[]}
                          value={validation.values.product_name}
                          inputValue={inputValue}
                          onInputChange={handleInputChange}
                          onKeyDown={handleKeyDown}
                          onPaste={handlePaste}
                          onChange={val =>
                            validation.setFieldValue("product_name", val)
                          }
                          classNamePrefix="select"
                          formatCreateLabel={value => `Add "${value}"`}
                          invalid={
                            validation.touched.product_name &&
                            validation.errors.product_name
                              ? true
                              : false
                          }
                        />
                        {validation.errors.product_name && (
                          <div className="text-danger small mt-1">
                            {validation.errors.product_name}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="d-flex justify-content-end">
                      <Button type="submit" color="primary">
                        Add Product
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default AddProduct
